import { useCallback, useEffect, useState } from 'react'
import { useProjectPlanSnapshotRepository } from '../../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { MasterScheduleRowData, ProgressValues } from '../model'
import { useProjectPrivateContext } from '../../../../../context/projectContext'
import {
  ProgressValue,
  ProjectPlanSnapshot,
} from '../../../../../../domain/value-object/ProjectPlanSnapshot'
import { aggregatedValueService } from '../../../../../../domain/value-object/AggregatedValueVO'
import {
  AggregateField,
  AggregateTarget,
  WbsItemType,
} from '../../../../../../domain/entity/WbsItemEntity'
import { resolveSnapshotWbsItemType } from '../../../lib/snapshotWbsItemType'

export const DEFAULT_AGGREGATE_TARGET: AggregateTarget = WbsItemType.TASK
export const DEFAULT_AGGREGATE_FIELD: AggregateField =
  AggregateField.WBS_ITEM_COUNT

export const computeProgressValues = (
  pv: ProgressValue,
  target: AggregateTarget,
  field: AggregateField
): ProgressValues => {
  const total = aggregatedValueService.getValue(pv.total, target, field)
  const completed = aggregatedValueService.getValue(pv.completed, target, field)
  const planned = aggregatedValueService.getValue(pv.planned, target, field)
  return {
    progressRate: total === 0 ? NaN : completed / total,
    scheduledProgressRate: total === 0 ? NaN : planned / total,
  }
}

export const useMasterScheduleData = (
  projectUuid: string,
  snapshotUuid: string,
  leafUuids: string[],
  callbackWhenStartLoading?: () => void,
  callbackWhenFinishLoading?: () => void
) => {
  const [rows, setRows] = useState<MasterScheduleRowData[]>([])
  const converter = useRowDataConverter()
  const { fetchByLeafUuid } = useProjectPlanSnapshotRepository()
  useEffect(() => {
    if (leafUuids.length === 0) {
      setRows([])
      return
    }
    const fn = async () => {
      callbackWhenStartLoading?.()
      const response = await fetchByLeafUuid(snapshotUuid, leafUuids)
      setRows(response.map(resp => converter(resp)))
      callbackWhenFinishLoading?.()
    }
    fn()
  }, [
    converter,
    fetchByLeafUuid,
    leafUuids,
    projectUuid,
    snapshotUuid,
    callbackWhenStartLoading,
    callbackWhenFinishLoading,
  ])
  return {
    rows,
  }
}

const useRowDataConverter = () => {
  const { wbsItemTypes } = useProjectPrivateContext()
  const converter = useCallback(
    (src: ProjectPlanSnapshot): MasterScheduleRowData => {
      const wbsItemType = resolveSnapshotWbsItemType(
        src.path,
        src.type,
        wbsItemTypes
      )
      return {
        uuid: src.uuid,
        type: src.type,
        treeValue: src.path,
        wbsItem: {
          ...src.wbsItem,
          type: src.type,
          wbsItemType,
        },
        progressValue: src.progressValue,
      }
    },
    [wbsItemTypes]
  )
  return converter
}
